import React, { useEffect, useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle, ArrowRight } from "lucide-react";
import { Wordmark } from "@/components/Brand";
import api from "@/lib/api";

const POLL_MAX = 8;
const POLL_INTERVAL_MS = 2000;

const CheckoutSuccess = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("checking"); // checking | paid | failed | expired
  const [info, setInfo] = useState(null);

  useEffect(() => {
    if (!sessionId) {
      setStatus("failed");
      return;
    }
    let attempts = 0;
    let stop = false;

    const poll = async () => {
      if (stop) return;
      attempts += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        setInfo(data);
        if (data.payment_status === "paid") {
          setStatus("paid");
          return;
        }
        if (data.status === "expired") {
          setStatus("expired");
          return;
        }
      } catch (e) {
        console.error(e);
      }
      if (attempts >= POLL_MAX) {
        setStatus("failed");
        return;
      }
      setTimeout(poll, POLL_INTERVAL_MS);
    };
    poll();
    return () => {
      stop = true;
    };
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-[#050B14] text-white flex flex-col" data-testid="checkout-success-page">
      <div className="px-6 lg:px-10 pt-6">
        <Link to="/" className="inline-flex items-center gap-2"><Wordmark className="h-12" /></Link>
      </div>

      <div className="flex-1 flex items-center justify-center px-6">
        <div className="max-w-lg w-full text-center">
          {status === "checking" && (
            <div data-testid="checkout-checking">
              <Loader2 className="mx-auto text-cyan-300 animate-spin" size={36} />
              <p className="mt-6 font-mono text-xs uppercase tracking-[0.3em] text-cyan-400">
                Verifying payment
              </p>
              <h1 className="mt-3 font-heading text-3xl font-black tracking-tighter">
                Securing your seat in the Flow...
              </h1>
            </div>
          )}

          {status === "paid" && (
            <div data-testid="checkout-paid">
              <CheckCircle2 className="mx-auto text-emerald-400" size={48} />
              <p className="mt-6 font-mono text-xs uppercase tracking-[0.3em] text-cyan-400">
                Payment received
              </p>
              <h1 className="mt-3 font-heading text-4xl font-black tracking-tighter">
                Welcome to ClydeFlow.
              </h1>
              <p className="mt-4 text-slate-300">
                You paid {info?.amount_total ? (info.amount_total / 100).toFixed(2) : ""}{" "}
                <span className="uppercase">{info?.currency}</span>. Your empire's automation suite is unlocked.
              </p>
              <button
                onClick={() => navigate("/dashboard")}
                data-testid="checkout-goto-dashboard"
                className="mt-8 inline-flex items-center gap-2 px-6 py-3 bg-[#00F0FF] text-[#050B14] font-bold rounded-sm hover:bg-[#33F3FF]"
              >
                Open Dashboard <ArrowRight size={16} />
              </button>
            </div>
          )}

          {(status === "failed" || status === "expired") && (
            <div data-testid="checkout-failed">
              <XCircle className="mx-auto text-red-400" size={48} />
              <p className="mt-6 font-mono text-xs uppercase tracking-[0.3em] text-red-300">
                {status === "expired" ? "Session expired" : "Couldn't confirm payment"}
              </p>
              <h1 className="mt-3 font-heading text-3xl font-black tracking-tighter">
                Let's try again.
              </h1>
              <Link
                to="/#pricing"
                data-testid="checkout-retry"
                className="mt-8 inline-flex items-center gap-2 px-6 py-3 bg-white/5 border border-white/15 text-white font-bold rounded-sm hover:bg-white/10"
              >
                Back to pricing
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CheckoutSuccess;
